"use client";
import { Button } from "@/components/ui/button";
import type { UserOptional } from "@/lib/auth";
import { useRouter } from "@/lib/hooks/useRouter";
import { type DeckResult, deckSearch } from "@/lib/queries/deckQueries";
import { signout } from "@/lib/signout";
import { useQuery } from "@tanstack/react-query";
import { CommandLoading } from "cmdk";
import { useEffect, useState } from "react";
import { DeckCommandItem } from "../../CommandItems/DeckCommandItem";
import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "../../ui/command";

interface Props {
	user?: UserOptional;
	latestUserDecks?: DeckResult[];
}

export const AppSearch: React.FC<Props> = ({ user, latestUserDecks }) => {
	const [search, setSearch] = useState("");
	const [open, setOpen] = useState(false);
	const router = useRouter();

	const { data: yourDecks, isLoading: yourDecksLoading } = useQuery({
		queryKey: ["your-decks", search],
		queryFn: async () => {
			return search.length > 0
				? await deckSearch(search, "only-current-user-decks")
				: latestUserDecks || [];
		},
		refetchOnMount: false,
		initialData: latestUserDecks || [],
		enabled: !!user,
	});

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpen((open) => !open);
			}
		};
		document.addEventListener("keydown", down);
		return () => document.removeEventListener("keydown", down);
	});

	const navigate = (path: string) => {
		router.push(path);
		setOpen(false);
	};

	const runAndClose = (fn: () => void) => {
		fn();
		setOpen(false);
	};

	return (
		<>
			<Button variant="outline" onClick={() => setOpen(true)}>
				<kbd className="kbd-sm">⌘</kbd>
				<kbd className="kbd-sm">K</kbd>
			</Button>
			<CommandDialog open={open} onOpenChange={setOpen}>
				<CommandInput
					placeholder="Search ..."
					value={search}
					onValueChange={setSearch}
				/>
				<CommandList>
					<CommandEmpty>No results found</CommandEmpty>
					{yourDecksLoading && (
						<CommandLoading>Searching your decks</CommandLoading>
					)}
					{yourDecks?.length > 0 && (
						<CommandGroup heading="Your decks">
							{yourDecks.map((deck) => (
								<DeckCommandItem
									key={deck.id}
									deck={deck}
									onSelect={() => navigate(`/decks/${deck.id}`)}
								/>
							))}
						</CommandGroup>
					)}
					{user && (
						<CommandGroup heading="Your account">
							<CommandItem onSelect={() => navigate("/user")}>
								Inventory
							</CommandItem>
							<CommandItem onSelect={() => navigate("/user/cards")}>
								Your cards
							</CommandItem>
							<CommandItem onSelect={() => navigate("/decks/personal")}>
								Your decks
							</CommandItem>
							<CommandItem onSelect={() => navigate("/user/settings")}>
								Settings
							</CommandItem>
							<CommandItem onSelect={() => runAndClose(signout)}>
								Logout
							</CommandItem>
						</CommandGroup>
					)}
					<CommandGroup heading="Suggestions">
						<CommandItem onSelect={() => navigate("/decks")}>Decks</CommandItem>
						<CommandItem
							onSelect={() => navigate("/decks/public?format=commanderPrecons")}
						>
							Commander precons
						</CommandItem>
						<CommandItem
							onSelect={() => navigate("/decks/public?format=precons")}
						>
							All precons
						</CommandItem>
						<CommandItem onSelect={() => navigate("/search")}>
							Search all cards
						</CommandItem>
					</CommandGroup>
				</CommandList>
			</CommandDialog>
		</>
	);
};
